import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useRef } from 'react';
import axiosClient from 'api/config/axios';
import { adaptTradeEscrow } from 'api/escrow/adapters';
import { TradeEscrow } from 'api/escrow/types';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { allPayoutsTerminal } from 'utils/payout-status';

/**
 * Determines whether a failed query should be retried.
 *
 * Cancellation signals and authorisation/not-found errors are treated as
 * terminal — no further attempts are made. All other errors are retried up
 * to three times, which guards against transient network failures.
 */
export function escrowRetry(failureCount: number, error: unknown): boolean {
  if (axios.isCancel(error)) return false;
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 401 || status === 403 || status === 404) return false;
  return failureCount < 3;
}

/**
 * Returns the delay in milliseconds before the next retry attempt.
 *
 * Uses exponential back-off capped at 30 seconds: 1s, 2s, 4s, …, 30s.
 */
export function escrowRetryDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 30000);
}

/**
 * Fetches escrow data for the given trade from the API.
 *
 * As of the backend's on-read sync change, every GET on this endpoint
 * automatically syncs any pending payments from the gateway before
 * responding (subject to a 60-second per-payment cooldown on the backend
 * side). Callers no longer need to force a sync when the user returns
 * from a payment provider — a plain fetch is sufficient.
 *
 * The optional `signal` supports request cancellation via AbortSignal,
 * which React Query passes automatically when a component unmounts
 * mid-request.
 */
export async function getEscrowByTrade(tradeId: number, signal?: AbortSignal): Promise<TradeEscrow> {
  const url = `/api/v1/escrow/trades/by-trade/${tradeId}`;
  const result = await axiosClient.query.get(url, { signal });
  return adaptTradeEscrow(result.data.escrow);
}

export const getEscrowByTradeQueryKey = (tradeId: number) => ['escrow', 'trade', tradeId] as const;

type Options = {
  enabled?: boolean;
  /** Poll while escrow is in PendingFunding state (pre-activation). */
  polling?: boolean;
  /** Poll for payout completion after trade completion (adaptive 15s→30s cadence, max 10 min). */
  payoutPolling?: boolean;
};

// adaptive payout polling: 15s for first 2 min, then 30s, max 10 min total
const PAYOUT_POLL_FAST_MS = 15_000;
const PAYOUT_POLL_SLOW_MS = 30_000;
const PAYOUT_POLL_FAST_END = 2 * 60 * 1000;
const PAYOUT_POLL_MAX_MS = 10 * 60 * 1000;

/**
 * React Query hook that wraps `getEscrowByTrade` for use in components.
 *
 * When `polling` is true the query refetches every 7 seconds, which keeps the
 * UI up-to-date while the escrow is in the PendingFunding state. Polling stops
 * automatically once the escrow reaches a terminal state (`activatedAt` or
 * `closedAt` is set) to avoid unnecessary gateway traffic.
 *
 * When `payoutPolling` is true the query uses an adaptive cadence (15s for the
 * first 2 minutes, then 30s) up to a maximum of 10 minutes. Polling stops
 * early when all payout components reach a terminal status.
 *
 * The `enabled` option allows callers to defer fetching until prerequisite
 * data (e.g. trade status) is available.
 */
export function useGetEscrowByTradeQuery(tradeId: number, options: Options = {}) {
  const { enabled = true, payoutPolling = false, polling = false } = options;
  const payoutPollingStartRef = useRef<number | null>(null);

  // reset polling timer when tradeId or payoutPolling flag changes
  useEffect(() => {
    payoutPollingStartRef.current = null;
  }, [tradeId, payoutPolling]);

  return useQuery(
    {
      queryKey: getEscrowByTradeQueryKey(tradeId),
      queryFn: ({ signal }) => getEscrowByTrade(tradeId, signal),
      enabled,
      refetchInterval: (query) => {
        const data = query.state.data;

        // pre-activation funding polling
        if (polling) {
          if (data?.activatedAt || data?.closedAt) return false;
          return 7000;
        }

        // post-completion payout polling
        if (payoutPolling) {
          // stop only if payouts are present and all components are terminal;
          // payouts === undefined means backend has not yet emitted payout data
          if (data?.payouts && allPayoutsTerminal(data.payouts)) {
            payoutPollingStartRef.current = null;
            return false;
          }

          const now = Date.now();
          if (payoutPollingStartRef.current === null) {
            payoutPollingStartRef.current = now;
          }

          const elapsed = now - payoutPollingStartRef.current;

          // stop after max duration
          if (elapsed >= PAYOUT_POLL_MAX_MS) {
            payoutPollingStartRef.current = null;
            return false;
          }

          return elapsed < PAYOUT_POLL_FAST_END ? PAYOUT_POLL_FAST_MS : PAYOUT_POLL_SLOW_MS;
        }

        return false;
      },
      refetchIntervalInBackground: false,
      retry: escrowRetry,
      retryDelay: escrowRetryDelay,
    },
    memoryQueryClient,
  );
}
